import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Grid, List, ListItem, Typography, ListItemText } from '@material-ui/core';
import {
  useStyles,
  listTextStyles,
  customCardStyles,
  collapseCardStyles,
} from './Reservations.styled';
import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import Loading from 'components/Loading';
import Icon from '@material-ui/core/Icon';
import { Card, CollapseCard } from 'components/UI';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { ReservationsActions } from 'state/modules/reservations';

const Status = ({ classes, status }) => {
  return (
    <Typography
      variant="inherit"
      className={`${classes.status} ${classes[status?.toLowerCase()] || ''}`}
    >
      {i18n.t(`reservations_statuses.${status}`, {
        defaultValue: status || '',
      })}
    </Typography>
  );
};

const ReservationsDetails = ({ id, timezone }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const cardClasses = customCardStyles();
  const collapseCardClasses = collapseCardStyles();
  const { reservation, reservationLoading } = useSelector((state) => state.reservations);
  const { localeFormat } = useSelector((state) => state.organizations);

  /**
   * Get reservation by id
   */
  const fetchReservationById = useCallback(
    (id) => {
      dispatch(ReservationsActions.fetchReservationById(id));
    },
    [dispatch]
  );

  /**
   * Parse reservation data
   * @param {object} reservation
   */
  const getReservationData = (reservation = {}) => {
    const reservationInfo = [
      {
        label: i18n.t('common.user'),
        value: reservation?.user
          ? `${reservation?.user?.first_name} ${reservation?.user?.last_name}`
          : '-',
      },
      {
        label: i18n.t('common.vehicle'),
        value: reservation?.vehicle ? `${reservation?.vehicle?.name}` : '-',
      },
      {
        label: i18n.t('reservations.total_distance'),
        value: reservation.distance
          ? `${reservation?.distance?.magnitude} ${reservation?.distance?.units}`
          : '-',
      },
    ];

    return reservationInfo;
  };

  useEffect(() => {
    fetchReservationById(id);
  }, [fetchReservationById, id]);

  const TimezoneComponent = () => (
    <span className={classes.timezone}>
      {timezone ? moment().tz(timezone).format('(UTC Z)') : ''}
    </span>
  );

  return (
    <Card
      headerActionType="vertical"
      data-cy="reservation-widget-card"
      contentRootClasses={cardClasses}
      title={i18n.t('reservations.details')}
    >
      {reservationLoading ? (
        <Box
          className={classes.loadingContainer}
          justifyContent="center"
          alignItems="center"
          display="flex"
        >
          <Loading />
        </Box>
      ) : (
        <Box className={classes.cardContentPadding}>
          <Grid container direction="row" alignItems="center" spacing={1}>
            <Grid item xs={4}>
              <Typography variant="h3" data-cy="details-id">
                <Box className={classes.name}>
                  {i18n.t('common.id')} #{id}
                </Box>
              </Typography>
            </Grid>
            {reservation?.status && (
              <Grid item xs={8} className={`${classes.textRight}`} data-cy="reservation-status">
                <Status classes={classes} status={reservation?.status} />
              </Grid>
            )}
            {reservation?.description && (
              <Grid item xs={12}>
                <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                  <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                    {i18n.t('common.description')}:
                  </Box>
                </Typography>
                <Typography>
                  <Box fontSize={13} component="span">
                    {reservation?.description}
                  </Box>
                </Typography>
              </Grid>
            )}
          </Grid>
          <Box className={classes.location} mt={3} data-cy="reservation-location">
            <Grid item xs={12} sm={12} md={12}>
              <Box position="relative" pb={3}>
                <Box className={classes.zoneLine}>
                  <Box className={classes.circle} />
                  <hr className={classes.dashedLine} />
                </Box>
                <Box pl={3}>
                  <Typography className={classes.textPrimary}>
                    <Box fontSize={13} component="span">
                      {i18n.t('reservations.action_buttons.start')}
                    </Box>
                  </Typography>
                  <Typography className={classes.textSubtitle}>
                    <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                      {i18n.t('reservations.pickup')}
                    </Box>
                  </Typography>
                  <Typography>
                    <Box mt={2} fontSize={13} component="span">
                      {reservation?.start_reserved_location
                        ? `${reservation?.start_reserved_location?.address} - ${reservation?.start_reserved_location?.name}`
                        : '-'}
                    </Box>
                  </Typography>
                  <Box mt={1}>
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.start_reserved
                              ? moment(reservation.start_reserved)
                                  .tz(timezone)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.start_reserved && <TimezoneComponent />}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item sm={12} md={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.used')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.start_used
                              ? moment(reservation.start_used)
                                  .tz(timezone)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.start_used && <TimezoneComponent />}
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
              <Box position="relative">
                <Box className={clsx(classes.zoneLine, classes.locationLineContainer)}>
                  <Icon component={LocationOnIcon} className={classes.locationIcon} />
                </Box>
                <Box pl={3}>
                  <Typography className={classes.textPrimary}>
                    <Box fontSize={13} component="span">
                      {i18n.t('reservations.action_buttons.end')}
                    </Box>
                  </Typography>
                  <Typography className={classes.textSubtitle}>
                    <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                      {i18n.t('reservations.dropoff')}
                    </Box>
                  </Typography>
                  <Typography>
                    <Box mt={2} fontSize={13} component="span">
                      {reservation?.start_reserved_location
                        ? `${reservation?.start_reserved_location?.address} - ${reservation?.start_reserved_location?.name}`
                        : '-'}
                    </Box>
                  </Typography>
                  <Box mt={1}>
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.reserved')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.end_reserved
                              ? moment(reservation.end_reserved)
                                  .tz(timezone)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.end_reserved && <TimezoneComponent />}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item sm={12} md={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('reservations.used')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {reservation?.end_used
                              ? moment(reservation.end_used)
                                  .tz(timezone)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {reservation?.end_used && <TimezoneComponent />}
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Box>
          <CollapseCard
            title={i18n.t('reservations.basic_information')}
            dataCy="reservation-widget-basic-info"
            customClasses={collapseCardClasses}
          >
            <List className={classes.list}>
              {getReservationData(reservation)?.map((val, index) => (
                <ListItem className={classes.listItem} key={index}>
                  <ListItemText
                    classes={listClasses}
                    primary={val.label}
                    secondary={
                      <Box
                        display="flex"
                        component="span"
                        flexDirection="column"
                        className={classes.textRight}
                      >
                        <Typography variant="body2" component="span" color="textSecondary">
                          {val.value}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CollapseCard>
        </Box>
      )}
    </Card>
  );
};

ReservationsDetails.propTypes = {
  id: PropTypes.number.isRequired,
  timezone: PropTypes.string,
};

ReservationsDetails.defaultProps = {
  timezone: 'UTC',
};

export default ReservationsDetails;
